import React, { useState } from 'react';
import { getApiBaseUrl } from '../utils/apiConfig';

const TenantDiagnostic: React.FC = () => {
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const testTenantAPI = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const API_BASE_URL = getApiBaseUrl();
      console.log('Testing API:', `${API_BASE_URL}/Tenants`);

      const response = await fetch(`${API_BASE_URL}/Tenants`);

      console.log('Response status:', response.status);
      console.log('Response OK:', response.ok);

      const data = await response.json();
      console.log('Response data:', data);

      setResult({
        status: response.status,
        ok: response.ok,
        data: data,
        apiUrl: `${API_BASE_URL}/Tenants`,
      });
    } catch (err: any) {
      console.error('Error:', err);
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Tenant API Diagnostic</h1>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Environment</h2>
          <dl className="space-y-2">
            <div>
              <dt className="font-semibold">REACT_APP_API_URL:</dt>
              <dd className="font-mono text-sm bg-gray-100 p-2 rounded">
                {process.env.REACT_APP_API_URL || 'Not set (using default)'}
              </dd>
            </div>
            <div>
              <dt className="font-semibold">Default API URL:</dt>
              <dd className="font-mono text-sm bg-gray-100 p-2 rounded">
                http://localhost:5200/api
              </dd>
            </div>
            <div>
              <dt className="font-semibold">Effective API URL:</dt>
              <dd className="font-mono text-sm bg-gray-100 p-2 rounded">
                {process.env.REACT_APP_API_URL || 'http://localhost:5200/api'}
              </dd>
            </div>
          </dl>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <button
            onClick={testTenantAPI}
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Testing...' : 'Test Tenant API'}
          </button>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h3 className="font-semibold text-red-800">Error</h3>
              <p className="text-red-700 mt-2">{error}</p>
            </div>
          )}

          {result && (
            <div className="mt-4 space-y-4">
              <div className={`p-4 rounded-lg ${result.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <h3 className="font-semibold">
                  Status: {result.status} {result.ok ? '✓' : '✗'}
                </h3>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">API URL Tested:</h3>
                <p className="font-mono text-sm">{result.apiUrl}</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Response Data:</h3>
                <pre className="text-xs overflow-auto bg-white p-3 rounded border">
                  {JSON.stringify(result.data, null, 2)}
                </pre>
              </div>

              {result.data?.data && Array.isArray(result.data.data) && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-blue-900 mb-2">
                    Tenants Found: {result.data.data.length}
                  </h3>
                  <ul className="list-disc list-inside space-y-1">
                    {result.data.data.map((tenant: any) => (
                      <li key={tenant.tenantId} className="text-blue-800">
                        {tenant.tenantName} ({tenant.tenantCode}) - ID: {tenant.tenantId}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-semibold text-yellow-900 mb-2">Troubleshooting Steps:</h3>
          <ol className="list-decimal list-inside space-y-2 text-yellow-800 text-sm">
            <li>Ensure the backend API is running on port 5200</li>
            <li>Check browser console for CORS errors</li>
            <li>Verify REACT_APP_API_URL environment variable if set</li>
            <li>Clear browser cache and localStorage</li>
            <li>Check Network tab in DevTools for failed requests</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default TenantDiagnostic;
